import React, { useEffect, useRef, useState } from 'react';
import { MainLayout } from 'components/layout/MainLayout';
import { Card, CardHeader, CardBody, Button, Modal, Input, Select } from 'components/ui';
import { usePermission } from 'hooks/usePermissions';
import { PageId, ActionCode } from 'types/index';
import { licensesService } from 'services/licenses.service';
import { License } from 'types/license';
import { companiesService } from 'services/companies.service';
import { packagesService } from 'services/packages.service';
import { DemoPackageId } from 'constants/index';
import { SelectOption } from 'types/index';
import { Package, getDetailByDuration } from 'types/package';
 

export const LicensesPage: React.FC = () => { 

  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [rows, setRows] = useState<License[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const [generateOpen, setGenerateOpen] = useState(false);
  const [company, setCompany] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [useCustomCompany, setUseCustomCompany] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [maxNbEmployees, setMaxNbEmployees] = useState<number | ''>('');
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [companyOptions, setCompanyOptions] = useState<SelectOption[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  const [packageOptions, setPackageOptions] = useState<SelectOption[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [packagesError, setPackagesError] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly' | ''>('');
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minFutureDate = tomorrow.toISOString().slice(0, 10);
  const hiddenDateInputRef = useRef<HTMLInputElement | null>(null);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await licensesService.list(page, pageSize);
        setRows(res.data);
        setTotal(res.pagination.total);
      } catch (e: any) {
        const friendly = 'An error occurred on the server. Please contact the administrator.';
        setError(e?.message || friendly);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    return () => {};
  }, [page]);

  const resetGenerateForm = () => {
    setCompany('');
    setCompanyId('');
    setValidUntil('');
    setMaxNbEmployees('');
    setGenerateError(null);
    setCompaniesError(null);
    setPackageOptions([]);
    setIsLoadingPackages(false);
    setPackagesError(null);
    setSelectedPackageId('');
    setSelectedPackage(null);
    setBillingPeriod('');
  };

  useEffect(() => {
    if (!generateOpen) return;
    let isMounted = true;
    const loadCompanies = async () => {
      setIsLoadingCompanies(true);
      setCompaniesError(null);
      try {
        // try to fetch a large page to include all companies
        const res = await companiesService.list(1, 1000);
        const options: SelectOption[] = res.data
          .filter((c: any) => !!c?.name)
          .map((c: any) => ({ value: c.id as string, label: c.name as string }));
        if (isMounted) setCompanyOptions(options);
      } catch (e: any) {
        if (isMounted) setCompaniesError(e?.message || 'Failed to load companies');
      } finally {
        if (isMounted) setIsLoadingCompanies(false);
      }
    };
    const loadPackages = async () => {
      setIsLoadingPackages(true);
      setPackagesError(null);
      try {
        const pkgs = await packagesService.getAll();
        const options: SelectOption[] = pkgs
          .filter((p: any) => !!p?.name) 
          .filter((p: any) => {
            const idNum = Number(p.id);
            return idNum !== DemoPackageId.Company && idNum !== DemoPackageId.Individual;
          })
          .map((p: any) => ({ value: String(p.id), label: String(p.name) }));
        if (isMounted) setPackageOptions(options);
      } catch (e: any) {
        if (isMounted) setPackagesError(e?.message || 'Failed to load packages');
      } finally {
        if (isMounted) setIsLoadingPackages(false);
      }
    };
    loadCompanies();
    loadPackages();
    return () => {
      isMounted = false;
    };
  }, [generateOpen]);

  const formatYmd = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const addMonths = (date: Date, months: number) => {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    // handle month overflow for shorter months
    if (d.getDate() !== day) {
      d.setDate(0);
    }
    return d;
  };

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  return (
    <MainLayout>
      <Card>
        <CardHeader title="Licenses"/>
        <CardBody>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="text-sm text-secondary-600">
              Page {page} of {totalPages}
            </div> 
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="primary"
                size="sm"
                onClick={() => setGenerateOpen(true)}
                className="w-full sm:w-auto"
                leftIcon={ 
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  > 
                    <path
                      fill="currentColor"
                      d="M6 2a2 2 0 0 0-2 2v16c0 1.1.9 2 2 2h7.5a6.5 6.5 0 0 1 0-2H6V4h8v5a1 1 0 0 0 1 1h5V9a1 1 0 0 0-.293-.707l-6-6A1 1 0 0 0 15 2H6zm9 1.414L19.586 8H15V3.414z"
                    /> 
                    <circle cx="18" cy="18" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M18 16v4M16 18h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg> 
                }
              >
                Generate
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-3 text-sm text-red-600">{error}</div>
          )}
          {isLoading && (
            <div className="mb-3 text-sm text-secondary-600">Loading...</div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {!isLoading && !error && rows.length === 0 && (
              <div className="p-6 text-center text-secondary-600">No data</div>
            )}
            {rows.map((r) => (
              <div
                key={r.id}
                className="bg-white border border-secondary-200 rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-secondary-900 text-base">
                      {r.companyName}
                    </h3>
                    <p className="text-sm text-secondary-600 mt-1">
                      Created by: {r.createdBy}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 ml-2">
                    <span
                      className={
                        `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ` +
                        (r.isInUse
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-red-100 text-red-700 border-red-200')
                      }
                    >
                      {r.isInUse ? 'In Use' : 'Not In Use'}
                    </span>
                    <span
                      className={
                        `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ` +
                        (r.isActive
                          ? 'bg-green-100 text-green-700 border-green-200'
                          : 'bg-red-100 text-red-700 border-red-200')
                      }
                    >
                      {r.isActive ? 'Active' : 'Not Active'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Max Employees:
                    </span>
                    <span className="text-secondary-900">{r.maxNbEmployees}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Valid Until:
                    </span>
                    <span className="text-secondary-900">{r.validUntilDateStr}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-secondary-500 font-medium min-w-[140px]">
                      Created Time:
                    </span>
                    <span className="text-secondary-900">{r.createdTimeStr}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border border-secondary-200 rounded-md overflow-hidden">
              <thead className="bg-secondary-50">
                <tr>
                  <th className="text-left p-3 text-secondary-700 font-semibold">Company Name</th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">Max Nb Employees</th> 
                  <th className="text-left p-3 text-secondary-700 font-semibold">Valid Until Date</th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">Created By</th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">Created Time</th>
                  <th className="text-left p-3 text-secondary-700 font-semibold">Usage</th> 
                  <th className="text-left p-3 text-secondary-700 font-semibold">Status</th> 
                </tr>
              </thead>
              <tbody>
                {!isLoading && !error && rows.length === 0 && (
                  <tr>
                    <td className="p-6 text-center text-secondary-600" colSpan={7}>No data</td>
                  </tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-secondary-100 hover:bg-secondary-50">
                    <td className="p-3">{r.companyName}</td>
                    <td className="p-3">{r.maxNbEmployees}</td>
                    <td className="p-3">{r.validUntilDateStr}</td> 
                    <td className="p-3">{r.createdBy}</td> 
                    <td className="p-3">{r.createdTimeStr}</td> 
                    <td className="p-3">
                      <span
                        className={
                          `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ` +
                          (r.isInUse
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-red-100 text-red-700 border-red-200')
                        }
                      >
                        {r.isInUse ? 'In Use' : 'Not In Use'}
                      </span>
                    </td> 
                    <td className="p-3">
                      <span
                        className={
                          `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ` +
                          (r.isActive
                            ? 'bg-green-100 text-green-700 border-green-200'
                            : 'bg-red-100 text-red-700 border-red-200')
                        }
                      >
                        {r.isActive ? 'Active' : 'Not Active'}
                      </span>
                    </td> 
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={page === 1} className="w-full sm:w-auto">
              Prev
            </Button>
            <div className="text-sm text-secondary-600 text-center">{(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}</div>
            <Button variant="outline" size="sm" onClick={goNext} disabled={page === totalPages} className="w-full sm:w-auto">
              Next
            </Button>
          </div>
        </CardBody>
      </Card>

      <Modal
        open={generateOpen}
        title="Generate License"
        confirmText="Generate"
        cancelText="Cancel"
        onConfirm={async () => {
          try {
            setGenerateError(null);
            if (!companyId || !selectedPackageId || !billingPeriod || !validUntil || maxNbEmployees === '' || maxNbEmployees === undefined || maxNbEmployees === null) {
              setGenerateError('All fields are required.');
              return;
            }
            const selected = new Date(`${validUntil}T00:00:00`);
            const minDate = new Date(`${minFutureDate}T00:00:00`);
            if (selected < minDate) {
              setGenerateError('Valid Until must be a future date.');
              return;
            }
            if (Number(maxNbEmployees) < 0) {
              setGenerateError('Max Nb Employees cannot be negative.');
              return;
            }
            const [y, m, d] = validUntil.split('-');
            const validUntilForApi = `${d}/${m}/${y}`;
            await licensesService.generate(companyId, validUntilForApi, Number(maxNbEmployees));
            try {
              const res = await licensesService.list(page, pageSize);
              setRows(res.data);
              setTotal(res.pagination.total);
            } catch (_) {}
            setGenerateOpen(false);
            resetGenerateForm();
          } catch (e) { 
          }
        }}
        onCancel={() => { setGenerateOpen(false); resetGenerateForm(); }}
      >
        <div className="space-y-3">
          {generateError && (
            <div className="text-sm text-red-600">{generateError}</div>
          )}
          
          <Select
            label="Company"
            required
            value={companyId}
            onChange={async (e) => {
              const selectedId = e.target.value;
              setCompanyId(selectedId);
              const selected = companyOptions.find(o => String(o.value) === String(selectedId));
              setCompany(selected ? String(selected.label) : '');
              setGenerateError(null);
            }}
            options={companyOptions}
            placeholder={isLoadingCompanies ? 'Loading companies…' : 'Select a company'}
            disabled={isLoadingCompanies}
          />      
          {companiesError && (
            <div className="text-sm text-red-600">{companiesError}</div>
          )}
          <Select
            label="Package"
            required
            value={selectedPackageId}
            onChange={(e) => {
              const pkgId = e.target.value;
              setSelectedPackageId(pkgId);
              setGenerateError(null);
              const pkgMeta = packageOptions.find(o => String(o.value) === String(pkgId));
              // We may not have the full object here; fetch from loaded list for accuracy
              // We'll compute values after we ensure we have the package
              (async () => {
                try {
                  const pkgObj = await packagesService.getById(pkgId);
                  setSelectedPackage(pkgObj);
                  if (typeof pkgObj?.numberOfUsers === 'number') {
                    setMaxNbEmployees(Math.max(0, Math.floor(pkgObj.numberOfUsers)));
                  }
                  // Set validUntil from package details (validityDays) when available
                  const isYearly = billingPeriod === 'yearly';
                  const detail = pkgObj ? getDetailByDuration(pkgObj, isYearly) : undefined;
                  const validityDays = detail?.validityDays ?? (isYearly ? 365 : 30);
                  setValidUntil(formatYmd(addDays(new Date(), Math.max(1, validityDays))));
                } catch (_) {
                  // ignore
                }
              })();
            }}
            options={packageOptions}
            placeholder={isLoadingPackages ? 'Loading packages…' : 'Select a package'}
            disabled={isLoadingPackages}
          />
          {packagesError && (
            <div className="text-sm text-red-600">{packagesError}</div>
          )}
          <Select
            label="Billing Period"
            required
            value={billingPeriod}
            onChange={(e) => {
              const period = e.target.value as 'monthly' | 'yearly' | '';
              setBillingPeriod(period);
              setGenerateError(null);
              // When period changes, recompute validUntil from selected package details (or default days)
              if (selectedPackageId && period) {
                const isYearly = period === 'yearly';
                const detail = selectedPackage ? getDetailByDuration(selectedPackage, isYearly) : undefined;
                const validityDays = detail?.validityDays ?? (isYearly ? 365 : 30);
                setValidUntil(formatYmd(addDays(new Date(), Math.max(1, validityDays))));
              }
            }}
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'yearly', label: 'Yearly' },
            ]}
            placeholder={'Select billing period'}
          />
          <Input
            label="Valid Until"
            type="text"
            required
            value={validUntil ? (() => { const [y,m,d] = validUntil.split('-'); return `${d}/${m}/${y}`; })() : ''}
            placeholder="dd/mm/yyyy"
            readOnly
          />
          <input
            ref={hiddenDateInputRef}
            type="date"
            min={minFutureDate}
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className="absolute opacity-0 -z-10 pointer-events-none h-0 w-0"
            tabIndex={-1}
            aria-hidden="true"
            disabled
          />
          <Input
            label="Max Nb Employees"
            type="number"
            required
            min={0}
            step={1}
            value={String(maxNbEmployees)}
            readOnly
          />
        </div>
      </Modal>
    </MainLayout>
  );
};

export default LicensesPage;


